// Composant réutilisable : affichage / édition de la grille tarifaire
// structurée. Utilisé à la fois sur la page Tarifs (standard global) et
// dans la modale "Tarifs du client" sur la fiche contact (personnalisation).
//
// Props :
//   data         : la structure tarif (voir defaultStructure côté backend)
//   onChange(d)  : appelé avec la structure modifiée (édition contrôlée)
//   readonly     : si true, désactive tous les champs

import { useState } from 'react';
import {
  Truck, Package, Sparkles,
  ChevronDown, ChevronRight,
} from 'lucide-react';

const BORNE_TYPES = [
  { key: 'spherik', label: 'Spherik', color: '#ff0fa4' },
  // Bleu plus contraste (sky-500) — l'ancien #0284c7 reste utilise ailleurs mais
  // ici on veut un ton qui ressort bien aussi sur fond blanc et fond clair.
  { key: 'classik', label: 'Classik', color: '#0ea5e9' },
  { key: 'prestige', label: 'Prestige', color: '#dc2626' },
];

// Local edit state per input: lets the user type a partial decimal like "12,"
// or "12." without losing the trailing separator. We only fire onChange with a
// numeric value once the input is "valid enough" (parseable).
function MoneyInput({ value, onChange, disabled, suffix = '€', highlight = false }) {
  // Display "12,3" instead of "12.3" while not in focus
  const format = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    return String(v).replace('.', ',');
  };
  const [draft, setDraft] = useState(null); // null = uncontrolled, use formatted value
  // Highlight: amber background + amber border to flag a field overriding the standard.
  const bg = highlight ? '#fef3c7' : (disabled ? '#f8fafc' : 'white');
  const border = highlight ? '1px solid #f59e0b' : '1px solid #d9d9d9';
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <input
        type="text"
        inputMode="decimal"
        value={draft !== null ? draft : format(value)}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^0-9.,]/g, '');
          setDraft(cleaned);
          const normalized = cleaned.replace(',', '.');
          if (normalized === '' || normalized === '.') { onChange(0); return; }
          const num = Number(normalized);
          if (Number.isFinite(num)) onChange(num);
        }}
        onBlur={() => setDraft(null)}
        disabled={disabled}
        title={highlight ? 'Valeur personnalisée (différente du tarif de base)' : undefined}
        style={{
          width: 90, padding: '6px 22px 6px 10px',
          border, borderRadius: 6,
          fontSize: 13, textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          background: bg,
          fontWeight: highlight ? 600 : 400,
          color: highlight ? '#92400e' : 'inherit',
        }}
      />
      <span style={{ position: 'absolute', right: 8, color: '#94a3b8', fontSize: 12, pointerEvents: 'none' }}>{suffix}</span>
    </div>
  );
}

// Compare two numeric values for "is overridden" — handles undefined/null/0
// gracefully so we only highlight real, intentional differences.
function isOverride(current, reference) {
  if (reference === undefined || reference === null) return false;
  const a = Number(current ?? 0);
  const b = Number(reference ?? 0);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) > 0.0001;
}

function ByTypeRow({ label, values, refValues, onChange, disabled }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr repeat(3, auto)', gap: 12, alignItems: 'center', padding: '6px 0' }}>
      <span style={{ fontSize: 13, color: '#475569' }}>{label}</span>
      {BORNE_TYPES.map((t) => (
        <div key={t.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: t.color, textTransform: 'uppercase', letterSpacing: 0.04 }}>{t.label}</span>
          <MoneyInput
            value={values?.[t.key] ?? 0}
            onChange={(v) => onChange(t.key, v)}
            disabled={disabled}
            highlight={isOverride(values?.[t.key], refValues?.[t.key])}
          />
        </div>
      ))}
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: 12,
      marginBottom: 14, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', padding: '14px 18px',
          border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
        }}
      >
        {open ? <ChevronDown size={16} color="#64748b" /> : <ChevronRight size={16} color="#64748b" />}
        {Icon && (
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={15} color="#6366f1" />
          </span>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{subtitle}</div>}
        </div>
      </button>
      {open && <div style={{ padding: '4px 18px 18px 50px', borderTop: '1px solid #f1f5f9' }}>{children}</div>}
    </section>
  );
}

// Helper pour modifier une valeur imbriquée (path = "a.b.c")
function setPath(obj, path, value) {
  const next = JSON.parse(JSON.stringify(obj || {}));
  const keys = path.split('.');
  let ref = next;
  for (let i = 0; i < keys.length - 1; i++) {
    ref[keys[i]] = ref[keys[i]] || {};
    ref = ref[keys[i]];
  }
  ref[keys[keys.length - 1]] = value;
  return next;
}

export default function TarifsGrid({ data, onChange, readonly = false, compareWith = null }) {
  const disabled = readonly;
  const set = (path, value) => onChange(setPath(data, path, value));
  if (!data) return null;
  // ref = standard reference, used to highlight overridden fields. When null
  // (page Tarifs standard) nothing is highlighted.
  const ref = compareWith;

  return (
    <div>
      <Section icon={Package} title="Missions pickup" subtitle="Tarif lorsque le client vient chercher la borne chez l'antenne.">
        <ByTypeRow
          label="Forfait pickup"
          values={data.pickup}
          refValues={ref?.pickup}
          onChange={(t, v) => set(`pickup.${t}`, v)}
          disabled={disabled}
        />
      </Section>

      <Section icon={Truck} title="Missions installations / livraisons" subtitle="Tarifs liés au déplacement et à la pose chez le client.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9', marginBottom: 6 }}>
          <span style={{ fontSize: 13, color: '#475569' }}>Barème kilométrique (km par défaut : 1)</span>
          <MoneyInput value={data.livraison?.bareme_km ?? 0} onChange={(v) => set('livraison.bareme_km', v)} disabled={disabled} suffix="€" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid #f1f5f9', marginBottom: 6 }}>
          <span style={{ fontSize: 13, color: '#475569', flex: 1 }}>Forfait minimum kilométrique</span>
          <MoneyInput value={data.livraison?.forfait_min_km ?? 0} onChange={(v) => set('livraison.forfait_min_km', v)} disabled={disabled} suffix="€" highlight={isOverride(data.livraison?.forfait_min_km, ref?.livraison?.forfait_min_km)} />
        </div>

        <ByTypeRow label="Forfait livraison aller" values={data.livraison?.forfait_aller} refValues={ref?.livraison?.forfait_aller} onChange={(t, v) => set(`livraison.forfait_aller.${t}`, v)} disabled={disabled} />
        <ByTypeRow label="Forfait livraison retour" values={data.livraison?.forfait_retour} refValues={ref?.livraison?.forfait_retour} onChange={(t, v) => set(`livraison.forfait_retour.${t}`, v)} disabled={disabled} />
        <ByTypeRow label="Forfait installation" values={data.livraison?.forfait_installation} refValues={ref?.livraison?.forfait_installation} onChange={(t, v) => set(`livraison.forfait_installation.${t}`, v)} disabled={disabled} />
        <ByTypeRow label="Forfait désinstallation" values={data.livraison?.forfait_desinstallation} refValues={ref?.livraison?.forfait_desinstallation} onChange={(t, v) => set(`livraison.forfait_desinstallation.${t}`, v)} disabled={disabled} />

        <div style={{ height: 12 }} />
        <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 4 }}>
          Tarifs majorés (nuit / weekend)
        </div>
        <ByTypeRow label="Forfait livraison aller majoré" values={data.livraison?.forfait_aller_majore} refValues={ref?.livraison?.forfait_aller_majore} onChange={(t, v) => set(`livraison.forfait_aller_majore.${t}`, v)} disabled={disabled} />
        <ByTypeRow label="Forfait livraison retour majoré" values={data.livraison?.forfait_retour_majore} refValues={ref?.livraison?.forfait_retour_majore} onChange={(t, v) => set(`livraison.forfait_retour_majore.${t}`, v)} disabled={disabled} />
        <ByTypeRow label="Forfait installation majoré" values={data.livraison?.forfait_installation_majore} refValues={ref?.livraison?.forfait_installation_majore} onChange={(t, v) => set(`livraison.forfait_installation_majore.${t}`, v)} disabled={disabled} />
        <ByTypeRow label="Forfait désinstallation majoré" values={data.livraison?.forfait_desinstallation_majore} refValues={ref?.livraison?.forfait_desinstallation_majore} onChange={(t, v) => set(`livraison.forfait_desinstallation_majore.${t}`, v)} disabled={disabled} />

        <div style={{ height: 12 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span style={{ fontSize: 13, color: '#475569', flex: 1 }}>Forfait minimum livraison par trajet aller</span>
          <MoneyInput value={data.livraison?.forfait_min_aller ?? 0} onChange={(v) => set('livraison.forfait_min_aller', v)} disabled={disabled} highlight={isOverride(data.livraison?.forfait_min_aller, ref?.livraison?.forfait_min_aller)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span style={{ fontSize: 13, color: '#475569', flex: 1 }}>Forfait minimum livraison par trajet retour</span>
          <MoneyInput value={data.livraison?.forfait_min_retour ?? 0} onChange={(v) => set('livraison.forfait_min_retour', v)} disabled={disabled} highlight={isOverride(data.livraison?.forfait_min_retour, ref?.livraison?.forfait_min_retour)} />
        </div>
      </Section>

      <Section icon={Sparkles} title="Animations spécifiques" subtitle="Prestations annexes liées à des animations.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span style={{ fontSize: 13, color: '#475569', flex: 1 }}>Forfait installation fond vert / photocall</span>
          <MoneyInput value={data.animation?.installation_fond_vert ?? 0} onChange={(v) => set('animation.installation_fond_vert', v)} disabled={disabled} highlight={isOverride(data.animation?.installation_fond_vert, ref?.animation?.installation_fond_vert)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span style={{ fontSize: 13, color: '#475569', flex: 1 }}>Forfait désinstallation fond vert / photocall</span>
          <MoneyInput value={data.animation?.desinstallation_fond_vert ?? 0} onChange={(v) => set('animation.desinstallation_fond_vert', v)} disabled={disabled} highlight={isOverride(data.animation?.desinstallation_fond_vert, ref?.animation?.desinstallation_fond_vert)} />
        </div>
      </Section>

    </div>
  );
}
